import React, { useState, useEffect } from 'react';
import { Search, Filter, Grid, List } from 'lucide-react';
import { useEvents } from '../context/EventContext_supabase';
import { useGenres } from '../hooks/useGenres';

const Events = () => {
  const { events } = useEvents();
  const { genres } = useGenres();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [lightboxImage, setLightboxImage] = useState(null);

  // ESC key handler for this component
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (lightboxImage) {
          setLightboxImage(null);
        } else if (selectedEvent) {
          setSelectedEvent(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [lightboxImage, selectedEvent]);

  // Format price to show 'FREE' for 0 or '0'
  const formatPrice = (price) => {
    if (!price || price === '0' || price === 0) {
      return 'FREE';
    }
    return price;
  };

  // Format time to 12-hour format
  const formatTime = (time) => {
    if (!time) return time;
    
    // If already in 12-hour format, return as is
    if (time.toLowerCase().includes('am') || time.toLowerCase().includes('pm')) {
      return time;
    }
    
    // Convert 24-hour to 12-hour format
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Sort artists function - bands first, then DJs
  const sortArtists = (artists) => {
    if (!artists) return [];
    return [...artists].sort((a, b) => {
      const typeOrder = { 'Band': 1, 'Artist': 2, 'Producer': 3, 'DJ': 4 };
      return (typeOrder[a.type] || 5) - (typeOrder[b.type] || 5);
    });
  };

  // Filter events by search and genre
  const filteredEvents = events.filter(event => {
    // Search by title, venue, artist name, or description
    const searchMatch = searchTerm.trim() === '' ||
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.artists && event.artists.some(artist => artist.name.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()));

    // Genre filter (support both genre and genres array)
    const genreMatch = !selectedGenre ||
      (event.genre && event.genre === selectedGenre) ||
      (event.genres && event.genres.includes(selectedGenre));

    return searchMatch && genreMatch;
  }).sort((a, b) => {
    // Sort by date - latest events first
    return new Date(b.date) - new Date(a.date);
  });

  return (
    <div className="page-container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="section-title">All Events</h1>
        <p style={{ textAlign: 'center', color: '#ccc' }}>
          Discover the best nights of punk, power pop and rock n roll
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)', 
        padding: '2rem', 
        borderRadius: '10px',
        marginBottom: '2rem',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          marginBottom: '1rem',
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '1rem',
          borderRadius: '8px'
        }}>
          <Search size={20} style={{ color: '#999' }} />
          <input
            type="text"
            placeholder="Search events, DJs, venues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input"
            style={{ 
              background: 'transparent', 
              border: 'none', 
              flex: 1,
              color: 'white'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Filter size={16} style={{ color: '#ff0080' }} />
          <select
            value={selectedGenre}
            onChange={(e) => setSelectedGenre(e.target.value)}
            className="form-select"
            style={{ minWidth: '150px' }}
          >
            <option value="">All Genres</option>
            {genres.map(genre => (
              <option key={genre.id} value={genre.name}>{genre.name}</option>
            ))}
          </select>
          
          {/* View Toggle Buttons */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                background: viewMode === 'grid' ? '#00ff80' : 'rgba(0, 255, 128, 0.2)',
                color: viewMode === 'grid' ? '#000000' : '#00ff80',
                border: '1px solid #00ff80',
                padding: '0.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                background: viewMode === 'list' ? '#00ff80' : 'rgba(0, 255, 128, 0.2)',
                color: viewMode === 'list' ? '#000000' : '#00ff80',
                border: '1px solid #00ff80',
                padding: '0.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div style={{ marginBottom: '1rem', color: '#999' }}>
        {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
        {searchTerm && ` for "${searchTerm}"`}
        {selectedGenre && ` in ${selectedGenre}`}
      </div>

      {/* Events List */}
      <div style={{ 
        display: viewMode === 'grid' ? 'grid' : 'flex',
        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fit, minmax(300px, 1fr))' : 'none',
        flexDirection: viewMode === 'list' ? 'column' : 'row',
        gap: viewMode === 'grid' ? '2rem' : '1rem'
      }}>
        {filteredEvents.length > 0 ? (
          filteredEvents.map(event => (
            <div key={event.id} className="event-card" style={{ 
              display: 'flex', 
              flexDirection: viewMode === 'grid' ? 'column' : 'row',
              height: viewMode === 'list' ? '250px' : undefined,
              minHeight: viewMode === 'list' ? '250px' : undefined
            }}>
              <img 
                src={event.image_url || event.image} 
                alt={event.title}
                className="event-image"
                style={{
                  width: viewMode === 'list' ? '300px' : '100%',
                  height: viewMode === 'list' ? '100%' : '200px',
                  objectFit: viewMode === 'list' ? 'contain' : 'cover',
                  flexShrink: 0,
                  backgroundColor: viewMode === 'list' ? 'rgba(0, 0, 0, 0.2)' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderRadius: '8px'
                }}
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop';
                }}
                onClick={() => setLightboxImage({ src: event.image_url || event.image, alt: event.title })}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 5px 20px rgba(0, 255, 128, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div className="event-content" style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                flex: 1,
                justifyContent: viewMode === 'list' ? 'space-between' : undefined
              }}>
                <h3 className="event-title">{event.title}</h3>
                <div className="event-meta">
                  <div>📅 {event.date}</div>
                  <div>🕒 {formatTime(event.time)}</div>
                  <div>📍 {typeof event.venue === 'object' ? event.venue.name : event.venue}</div>
                  <div>💰 {formatPrice(event.price || event.ticketPrice)}</div>
                </div>
                {event.genre && (
                  <div className="genre-tag">{typeof event.genre === 'object' ? event.genre.name : event.genre}</div>
                )}
                <p className="event-description">
                  {event.description.length > 100 
                    ? `${event.description.substring(0, 100)}...` 
                    : event.description
                  }
                </p>
                {event.artists && event.artists.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#ff00ff' }}>Artists:</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {sortArtists(event.artists).map(artist => (
                        <span key={artist.id} className="artist-tag">
                          {artist.name} ({artist.type})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="event-actions" style={{ 
                  marginTop: viewMode === 'list' ? '0' : 'auto', 
                  paddingTop: '1rem',
                  alignSelf: viewMode === 'list' ? 'flex-end' : undefined,
                  minWidth: viewMode === 'list' ? '150px' : undefined
                }}>
                  <button 
                    style={{
                      position: 'relative',
                      zIndex: 20,
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      color: '#00ff80',
                      border: '2px solid #00ff80',
                      padding: '0.8rem 1.5rem',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      borderRadius: '5px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 0 15px rgba(0, 255, 128, 0.5)',
                      transition: 'all 0.3s ease',
                      display: 'block',
                      width: '100%',
                      cursor: 'pointer',
                      textShadow: '0 0 10px rgba(0, 255, 128, 0.8)',
                      backdropFilter: 'blur(3px)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#00ff80';
                      e.target.style.color = '#000000';
                      e.target.style.boxShadow = '0 0 20px rgba(0, 255, 128, 0.8)';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.textShadow = 'none';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                      e.target.style.color = '#00ff80';
                      e.target.style.boxShadow = '0 0 15px rgba(0, 255, 128, 0.5)';
                      e.target.style.transform = 'translateY(0px)';
                      e.target.style.textShadow = '0 0 10px rgba(0, 255, 128, 0.8)';
                    }}
                    onClick={() => setSelectedEvent(event)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ 
            gridColumn: '1 / -1',
            textAlign: 'center', 
            padding: '3rem',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '10px'
          }}>
            <h3 style={{ color: '#00ffff' }}>No events found</h3>
            <p style={{ color: '#ccc' }}>Events will appear here once they're added through the admin dashboard.</p>
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          backdropFilter: 'blur(10px)'
        }} onClick={() => setSelectedEvent(null)}>
          <div style={{
            backgroundColor: 'rgba(10, 10, 10, 0.95)',
            border: '2px solid #00ff80',
            borderRadius: '15px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '1200px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 0 50px rgba(0, 255, 128, 0.3)'
          }} className="event-modal" onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button 
              onClick={() => setSelectedEvent(null)}
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                background: 'linear-gradient(135deg, #00ff80, #00cc66)',
                border: 'none',
                color: '#000000',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                boxShadow: '0 5px 20px rgba(0, 255, 128, 0.5)',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                zIndex: 1001
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1) rotateZ(90deg)';
                e.target.style.boxShadow = '0 8px 25px rgba(0, 255, 128, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1) rotateZ(0deg)';
                e.target.style.boxShadow = '0 5px 20px rgba(0, 255, 128, 0.5)';
              }}
            >
              ×
            </button>

            <div style={{
              display: 'flex',
              height: '100%',
              maxHeight: '85vh',
              overflow: 'hidden'
            }} className="event-modal-content">
              
              {/* Left Side - Flyer Image */}
              <div style={{
                flex: '0 0 50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                background: 'linear-gradient(135deg, rgba(0, 255, 128, 0.05), rgba(0, 0, 0, 0.3))'
              }} className="event-modal-image">
                <img 
                  src={selectedEvent.image_url || selectedEvent.image} 
                  alt={selectedEvent.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '10px',
                    boxShadow: '0 0 30px rgba(0, 255, 128, 0.3)',
                    border: '2px solid rgba(0, 255, 128, 0.3)'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop';
                  }}
                />
              </div>

              {/* Right Side - Event Details */}
              <div style={{
                flex: 1,
                padding: '2rem',
                overflowY: 'auto',
                color: 'white'
              }} className="event-modal-details">
                <h1 style={{
                  fontSize: '2.5rem',
                  color: '#00ff80',
                  marginBottom: '1rem',
                  textShadow: '0 0 20px rgba(0, 255, 128, 0.5)'
                }} className="event-modal-title">
                  {selectedEvent.title}
                </h1>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                  marginBottom: '2rem',
                  padding: '1.5rem',
                  background: 'rgba(0, 255, 128, 0.05)',
                  borderRadius: '10px',
                  border: '1px solid rgba(0, 255, 128, 0.2)'
                }} className="event-modal-grid">
                  <div>
                    <strong style={{ color: '#00ff80' }}>📅 Date:</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>{selectedEvent.date}</p>
                  </div>
                  <div>
                    <strong style={{ color: '#00ff80' }}>🕒 Time:</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>{formatTime(selectedEvent.time)}</p>
                  </div>
                  <div>
                    <strong style={{ color: '#00ff80' }}>📍 Venue:</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>{typeof selectedEvent.venue === 'object' ? selectedEvent.venue.name : selectedEvent.venue}</p>
                  </div>
                  <div>
                    <strong style={{ color: '#00ff80' }}>💰 Price:</strong>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {formatPrice(selectedEvent.price || selectedEvent.ticketPrice)}
                    </p>
                  </div>
                </div>

                {selectedEvent.genre && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{
                      background: 'rgba(0, 255, 128, 0.2)',
                      color: '#00ff80',
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      display: 'inline-block',
                      border: '1px solid #00ff80',
                      fontWeight: 'bold'
                    }}>
                      {typeof selectedEvent.genre === 'object' ? selectedEvent.genre.name : selectedEvent.genre}
                    </div>
                  </div>
                )}

                {selectedEvent.description && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ color: '#00ff80', marginBottom: '1rem' }}>Description</h3>
                    <p style={{ lineHeight: '1.6', color: '#ddd' }}>{selectedEvent.description}</p>
                  </div>
                )}

                {selectedEvent.artists && selectedEvent.artists.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ color: '#00ff80', marginBottom: '1rem' }}>Artists</h3>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {sortArtists(selectedEvent.artists).map(artist => (
                        <div key={artist.id} style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <h4 style={{ color: '#ff0080', margin: 0 }}>{artist.name}</h4>
                            <span style={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '0.2rem 0.8rem',
                              borderRadius: '15px',
                              fontSize: '0.8rem'
                            }}>
                              {artist.type}
                            </span>
                          </div>
                          {artist.bio && (
                            <p style={{ color: '#ccc', fontSize: '0.9rem', lineHeight: '1.4', margin: '0.5rem 0' }}>{artist.bio}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            backdropFilter: 'blur(10px)',
            animation: 'lightboxFadeIn 0.4s ease-out',
            cursor: 'pointer'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <style>{`
            @keyframes lightboxFadeIn {
              from { 
                opacity: 0; 
                backdrop-filter: blur(0px);
              }
              to { 
                opacity: 1; 
                backdrop-filter: blur(10px);
              }
            }
            
            @keyframes imageZoomIn {
              from { 
                opacity: 0;
                transform: perspective(1000px) rotateY(-15deg) rotateX(10deg) scale(0.8);
              }
              to { 
                opacity: 1;
                transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1);
              }
            }
            
            @keyframes imageFloat {
              0%, 100% { 
                transform: perspective(1000px) translateY(0px) rotateY(0deg);
              }
              50% { 
                transform: perspective(1000px) translateY(-10px) rotateY(2deg);
              }
            }
          `}</style>
          
          <div
            style={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              animation: 'imageZoomIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
              transformStyle: 'preserve-3d'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '15px',
                boxShadow: `
                  0 25px 50px rgba(0, 255, 128, 0.3),
                  0 15px 35px rgba(0, 255, 128, 0.2),
                  0 5px 15px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1)
                `,
                border: '2px solid rgba(0, 255, 128, 0.5)',
                animation: 'imageFloat 4s ease-in-out infinite',
                filter: 'brightness(1.1) contrast(1.05)',
                transformStyle: 'preserve-3d'
              }}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop';
              }}
            />
            
            {/* Close Button */}
            <button 
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                background: 'linear-gradient(135deg, #00ff80, #00cc66)',
                border: 'none',
                color: '#000000',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                boxShadow: '0 5px 20px rgba(0, 255, 128, 0.5)',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1) rotateZ(90deg)';
                e.target.style.boxShadow = '0 8px 25px rgba(0, 255, 128, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1) rotateZ(0deg)';
                e.target.style.boxShadow = '0 5px 20px rgba(0, 255, 128, 0.5)';
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
